import { z } from 'zod';
/**
 * AI Settings & Controls (spec §13.3) — the contracts the screen and the API share.
 *
 * Human-in-the-loop is NOT a setting and appears nowhere below: ADR-0008 enforces
 * it at the API for every feature, and a switch for it would be a switch for the
 * one thing that may not be switched off.
 */
export const AI_PROVIDERS = ['anthropic', 'gemini', 'local', 'stub'];
/** Providers whose calls leave Algihaz's own systems (OPEN_QUESTIONS Q8). */
export const EXTERNAL_AI_PROVIDERS = ['anthropic', 'gemini'];
/** Providers that take an API key, which the vault can hold. */
export const KEYED_AI_PROVIDERS = ['anthropic', 'gemini'];
/** Every AI-assisted feature, by app, with the words the screen uses for it. */
export const AI_FEATURES = {
    qaqc: {
        quality_brief: 'Draft the Quality Assessment Brief',
        prompt_to_transaction: 'Fill in a Document Governance Log entry from a sentence',
    },
    'lesson-learned': {
        rephrase: 'Rephrase the narrative fields',
        prompt_to_transaction: 'Fill in a new lesson from a sentence',
    },
    audit: { prompt_to_transaction: 'Raise a finding from a sentence' },
};
export const aiSettingsUpdateSchema = z.object({
    /** Null switches AI off for the app — every form falls back to manual entry. */
    provider: z.enum(AI_PROVIDERS).nullable().optional(),
    model: z.string().trim().min(1).max(120).nullable().optional(),
    featureToggles: z.record(z.string(), z.boolean()).optional(),
    guardrails: z
        .object({
        /** App 1: send the project's name to the provider, or only its code. */
        sendProjectNames: z.boolean().optional(),
    })
        .optional(),
    /** Suggestions per calendar month for this app; null = no quota. */
    monthlyQuota: z.number().int().min(1).max(1_000_000).nullable().optional(),
});
/**
 * Prompt-to-transaction for a form whose record gets its id from the server
 * (App 1's log entry, App 3's finding; pass 8). Only `prompt` goes to the provider.
 */
export const formPromptRequestSchema = z.object({
    prompt: z.string().trim().min(10, 'Describe it in a sentence or two.').max(2000),
});
/** What became of a form draft. The server checks `recordId` against what was saved. */
export const formPromptDispositionSchema = z.object({
    claimed: z.enum(['accepted', 'edited', 'rejected']),
    /** The record the person saved from the draft; absent when they discarded it. */
    recordId: z.string().uuid().nullable().optional(),
    answers: z.record(z.string(), z.string()).optional(),
});
export const aiCredentialSchema = z.object({
    /** Write-only. Never returned, never logged; only its last four characters are kept in the clear. */
    secret: z.string().trim().min(8, 'That is too short to be an API key.').max(500),
});
export const aiPriceSchema = z.object({
    inputPerMillion: z.number().min(0).max(100_000),
    outputPerMillion: z.number().min(0).max(100_000),
    /** ISO 4217, e.g. USD or SAR. */
    currency: z.string().trim().regex(/^[A-Z]{3}$/, 'Use a three-letter currency code, e.g. USD.'),
});
export const aiActivityQuerySchema = z.object({
    feature: z.string().trim().min(1).max(60).optional(),
    disposition: z.enum(['pending', 'accepted', 'edited', 'rejected', 'timed_out', 'failed']).optional(),
    before: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
//# sourceMappingURL=ai-settings.js.map